import * as React from 'react';
import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router';
import { link2 } from 'ui/theme';

const StyledLink = styled.a`
  ${link2}
  color: inherit;
  text-decoration-color: transparent;
  font-weight: 400;
`;

type Props = {
  children: React.ReactNode;
  /** @deprecated use `searchParams` */
  searchParamKey?: string;
  /** @deprecated use `searchParams` */
  searchParamValue?: string;
  /** List of search params and their values */
  searchParams?: Record<string, string>;
  deleteSearchParams?: string[];
};

/**
 * A search param link is used when a page element triggers a navigation event that needs to bypass typical link behavior. Examples include clicking an `<a>` tag that will modify the URL to trigger linked page behavior (e.g. changing a filter value that is syndicated to a URL search param) without a refresh.
 * @returns
 */
export const SearchParamLink = ({
  children,
  searchParamKey,
  searchParamValue,
  searchParams,
  deleteSearchParams = [],
  ...props
}: Props) => {
  const location = useLocation();
  const history = useHistory();
  const hrefUrl = new URLSearchParams(location.search || '');

  if (searchParamKey && searchParamValue) {
    hrefUrl.set(searchParamKey, searchParamValue);
  }

  if (searchParams) {
    Object.entries(searchParams).forEach(([key, value]) => {
      hrefUrl.set(key, value);
    });
  }

  if (deleteSearchParams.length > 0) {
    deleteSearchParams.forEach((param) => hrefUrl.delete(param));
  }

  const path = `${location.pathname}?${hrefUrl.toString()}`;

  const onClick = (linkClickEvent: React.MouseEvent) => {
    const linkElement = linkClickEvent.currentTarget;

    /**
     * Determine if the current frame will redirect
     * pressing secondary keys will avoid default link click behavior
     * target="_blank" will operate in a new window
     */
    const isMetaKeyPressed =
      linkClickEvent.metaKey ||
      linkClickEvent.altKey ||
      linkClickEvent.ctrlKey ||
      linkClickEvent.shiftKey;
    const isBlankLinkTarget = linkElement.getAttribute('target') === '_blank';
    const currentFrameWillRedirect = !isMetaKeyPressed && !isBlankLinkTarget;

    if (currentFrameWillRedirect) {
      linkClickEvent.preventDefault();
      linkClickEvent.stopPropagation();
      history.push(path);
    }
  };

  return (
    <StyledLink data-test="search-param-link" href={path} onClick={onClick} {...props}>
      {children}
    </StyledLink>
  );
};
